import {
  DOCUMENT,
  Inject,
  Injectable,
  Injector,
  PLATFORM_ID,
  inject,
} from '@angular/core';
import { EnvToken } from '@app/core/env.tokens';
import { isPlatformBrowser } from '@angular/common';
import * as en from 'assets/i18n/en.json';
import * as nl from 'assets/i18n/nl.json';
import slugify from 'slugify';
import { MatDialog } from '@angular/material/dialog';
import { LoaderModalComponent } from '@components/shared/loader-modal';
import { TranslateService } from '@services/logic/translate.service';
import { LocalizationService } from '@services/logic/localization.service';

const defaultPageOptions = {
  target: '_self',
} as const;

type PageOptions = {
  target?: '_self' | '_blank' | '_parent' | '_top';
};

type ProfilePageOptions = PageOptions & {
  userId: number | string;
};

type TasksPageOptions = PageOptions & {};

type TaskPageOptions = PageOptions & {
  title: string;
  city: string;
  taskId: string | number;
};

type SignupRPageOptions = PageOptions & {
  priorUrl?: string;
};

type CreateTaskPageOptions = PageOptions & {
  title?: string;
  description?: string;
  titleLabel?: string;
  heroImage?: string;
  heroTitle?: string;
  budget?: string | number;
  toolsNeeded?: boolean;
  campaign?: string;
};

/**
 * Linking to the Handyhand platform.
 *
 * All platform-targeting paths come from `@handyhand/i18n` via
 * `LocalizationService.route()` — never from translation JSON or hardcoded
 * strings. The platform base href comes from `EnvToken.platformUrl`.
 */
@Injectable({
  providedIn: 'root',
})
export class ExternalService {
  protected readonly env = inject(EnvToken);
  private readonly loc = inject(LocalizationService);

  private readonly handyhandPlatformBaseHref = this.env.platformUrl;
  readonly landingUrl = this.handyhandPlatformBaseHref;
  private isBrowser: boolean;

  constructor(
    @Inject(PLATFORM_ID) private platformId: Object,
    @Inject(DOCUMENT) private document: Document,
    private injector: Injector,
    private translateService: TranslateService,
  ) {
    this.isBrowser = isPlatformBrowser(this.platformId);
  }

  /** Absolute URL to the create-task flow on the platform, ready for `[href]` binding. */
  get createTaskUrl(): string {
    return this.platformUrl(this.loc.route('tasks.create'), {
      createTask: 'Title',
    });
  }

  /** Absolute URL to the tasks overview on the platform. */
  get tasksUrl(): string {
    return this.platformUrl(this.loc.route('tasks.overview'));
  }

  /** Absolute URL to the account-settings page on the platform. */
  get accountSettingsUrl(): string {
    return this.platformUrl(this.loc.route('settings.personal'));
  }

  /**
   * Absolute URL to the security settings (change password) on the platform,
   * via the login page: the settings sit behind the auth guard, which bounces
   * logged-out visitors to explore. The login page honours `redirectUrl` for
   * both states — logged out, it opens the login dialog and continues there
   * after sign-in; logged in, its guard forwards straight to it.
   */
  get securitySettingsUrl(): string {
    return this.platformUrl(this.loc.route('signup'), {
      redirectUrl: this.loc.route('settings.security'),
    });
  }

  /**
   * Absolute URL to a task deep-link (`/opgaver/link/:id`). Uses `tasks.view`
   * with `slug = 'link'` — the platform resolves the literal `link` slug as
   * an ID-only redirect to the real task page.
   */
  taskLinkUrl(taskId: string | number): string {
    return this.platformUrl(
      this.loc.route('tasks.view', { slug: 'link', id: taskId }),
    );
  }

  /** Absolute URL to a task page on the platform, from the task's stored slug. */
  taskUrl(slug: string, taskId: string | number): string {
    return this.platformUrl(
      this.loc.route('tasks.view', { slug: slug || 'link', id: taskId }),
    );
  }

  /** Absolute URL to the damage compensation claim form on the platform. */
  get damageCompensationApplicationUrl(): string {
    return this.platformUrl(this.loc.route('insurance.apply'));
  }

  /** Absolute URL to a help-centre article on the platform. */
  helpArticleUrl(params: {
    referrer: string;
    headpage: string;
    subpage?: string;
  }): string {
    const { referrer, headpage, subpage } = params;
    return this.platformUrl(
      subpage
        ? this.loc.route('help.referrer.headpage.subpage', {
            referrer,
            headpage,
            subpage,
          })
        : this.loc.route('help.referrer.headpage', { referrer, headpage }),
    );
  }

  /** Absolute URL to a public profile page on the platform. */
  profileUrl(userId: string | number): string {
    return this.platformUrl(this.loc.route('profile', { id: userId }));
  }

  private platformUrl(
    path: string,
    query?: Record<string, string | number | boolean>,
  ): string {
    const url = `${this.handyhandPlatformBaseHref}${path}`;
    if (!query) return url;
    const search = new URLSearchParams();
    for (const [key, value] of Object.entries(query)) {
      search.append(key, String(value));
    }
    const qs = search.toString();
    return qs ? `${url}?${qs}` : url;
  }

  /**
   * slugify uses ISO 639-1 locales; app uses `dk` for Danish JSON while slugify expects `da`.
   */
  private slugifyLocale(): string {
    const data = this.translateService.data;
    if (data === en) return 'en';
    if (data === nl) return 'nl';
    return 'da';
  }

  goToCreateTask(options: CreateTaskPageOptions = defaultPageOptions) {
    if (!this.isBrowser) return false;

    const dialogRef = this.showLoaderModal();

    const query: Record<string, string | number | boolean> = {
      createTask: 'Title',
      step: 0,
    };

    if (options.title) {
      query['taskTitle'] = options.title;
    }

    if (options.description) {
      query['taskDescription'] = options.description;
    }

    if (options.titleLabel) {
      query['titleLabel'] = options.titleLabel;
    }

    if (options.budget !== undefined) {
      query['budget'] = options.budget;
    }

    if (options.toolsNeeded !== undefined) {
      query['toolsNeeded'] = options.toolsNeeded;
    }

    if (options.heroTitle) {
      query['heroTitle'] = options.heroTitle;
    }

    if (options.heroImage) {
      query['heroImage'] = options.heroImage;
    }

    if (options.campaign) {
      query['campaign'] = options.campaign;
    }

    const url = this.platformUrl(this.loc.route('tasks.create'), query);

    // Add a small delay to ensure the loader is visible
    setTimeout(() => {
      window.open(url, options?.target || '_self');
      // Close the loader after a short delay to ensure the new page has started loading
      setTimeout(() => {
        if (dialogRef) {
          dialogRef.close();
        }
      }, 1000);
    }, 300);

    return true;
  }

  /**
   * CTA on a category page (`/hh`, `/service` hero). Same as `goToCreateTask`,
   * except the page h1 (`titleLabel`) also travels as `taskTitle`, so
   * handyhand-web prefills the title input with it rather than only showing
   * it as the placeholder. `titleLabel` is still sent, so the example
   * survives if the user clears the field. An explicit `title` always wins.
   *
   * Shipped from the `exp-visible-title-prefill-for-category-page--320001`
   * PostHog experiment, where this was the winning `test` variant.
   */
  goToCreateTaskFromCategoryPage(options: CreateTaskPageOptions) {
    if (options.titleLabel && !options.title) {
      return this.goToCreateTask({ ...options, title: options.titleLabel });
    }
    return this.goToCreateTask(options);
  }

  private showLoaderModal() {
    try {
      const dialog = this.injector.get(MatDialog);
      return dialog.open(LoaderModalComponent, {
        disableClose: true,
        panelClass: 'loader-modal-dialog',
        data: {
          message: 'GENERAL.LOADING.CREATING_TASK',
        },
      });
    } catch (error) {
      return null;
    }
  }

  public goToTasks(options: TasksPageOptions = defaultPageOptions) {
    if (!this.isBrowser) return;
    window.open(this.tasksUrl, options?.target || '_self');
  }

  public goToTask(options: TaskPageOptions) {
    if (!this.isBrowser) return;

    const { title, city, taskId } = options;
    const slug = slugify(title + ' ' + city, {
      lower: true,
      remove: /[*+~.()'"!:@/]/g,
      locale: this.slugifyLocale(),
    });
    const url = this.platformUrl(
      this.loc.route('tasks.view', { slug, id: taskId }),
    );
    window.open(url, options?.target || '_self');
  }

  public goToProfile(options: ProfilePageOptions) {
    if (!this.isBrowser) return;
    window.open(this.profileUrl(options.userId), options?.target || '_self');
  }

  public goToAccountSettings(options?: PageOptions) {
    if (!this.isBrowser) return;
    window.open(this.accountSettingsUrl, options?.target || '_self');
  }

  public goToLogin(options?: PageOptions) {
    if (!this.isBrowser) return;
    const url = this.platformUrl(this.loc.route('signup'));
    window.open(url, options?.target || '_self');
  }

  public goToSignup(options: SignupRPageOptions = defaultPageOptions) {
    if (!this.isBrowser) return false;

    let url = this.platformUrl(this.loc.route('signup'));
    if (options && options.priorUrl) {
      url += `?${options.priorUrl}`;
    }
    window.open(url, options?.target || '_self');
    return true;
  }

  public goToLanding(options: PageOptions = defaultPageOptions) {
    if (!this.isBrowser) return;
    window.open(this.handyhandPlatformBaseHref, options?.target || '_self');
  }

  public goToPrevious(delta = -1) {
    if (!this.isBrowser) return;

    if (this.document.referrer?.includes(this.env.platformUrl)) {
      window.history.go(delta);
    } else {
      this.goToLanding();
    }
  }
}
